import { FC, ReactElement, useEffect, useMemo, useState, PropsWithChildren, useRef } from 'react'
import { GetStaticPropsContext } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import clsx from 'clsx'
import { getDownloadHome, TDownloadHome, DownloadListItem, DownloadSecondItem } from '@service/download.service'
import { SEOData } from '@service/seo.service'
import retriveMessages, { LocaleType } from '@messages/message'
import { findRegionByLocale } from '@common/region-locale'
import { NextPageWithLayout } from '@common/common-types'
import { withSEO } from '@common/with-seo'
import { useMobileDetector } from '@hooks/use-mobile-detector'
import PageLayout from '@components/page-layout/page-layout'
import PdImage from '@components/pd-image/pd-image'
import SlidingTabs, { SlidingTabsRef } from '@components/standard/pd-sliding-tabs'
import FormBeforeDownload from '@components/standard/form-before-download'
import { Toaster, toast } from 'react-hot-toast'
import MobileCascader from './cascader.component'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import styles from './cascader.module.scss'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { getLinkRelForHref } from '@/common/link-rel-helper'
export interface IAPPCard {
  QRCodeUrl: string
  sysIcon: string
  sysName: string
  uploadUrl?: string
}

export interface IBaseCard {
  mediaUrl?: string
  title: string
  downloadUrl: string
  fileType: string
  size: string
  downloadTitle: string
}

enum DownloadType {
  'Brochures & Flyers' = 'Brochures & Flyers',
  'Insights & E-books' = 'Insights & E-books',
  'PUDU Link APP' = 'PUDU Link APP',
}

export const cdnUrl = 'https://cdn.pudutech.com/official-website'

interface TabFrameProps<T = {}> {
  init: boolean
  title: string
  desc?: string
  secondTitle: string
  thirdTitle: string
  data: Array<DownloadSecondItem>
  handleSubTitleChange: (title: string) => void
  handleThirdTitleChange: (title: string) => void
}

type TabPropsOfBaseType = TabFrameProps<DownloadListItem> & {
  init: boolean
  list: Array<DownloadListItem>
  startDownload: (url: string, filename: string) => void
}

type DownloadMetaType = {
  show: boolean
  url: string
  filename: string
}

const BaseCard: FC<
  Partial<{ data: DownloadListItem }> & {
    shareParams: { firstTitle: string; secondTitle: string; thirdTitle: string }
    startDownload: (url: string, filename: string) => void
  }
> = ({ data, shareParams, startDownload }) => {
  const router = useRouter()
  const { fileName } = router.query

  const t = useTranslations('Page')
  const cardRef = useRef<HTMLDivElement>(null)
  const fileSize = useMemo<string>(() => {
    return data?.size ? `-${(data?.size / 1024).toFixed(2)} MB` : ''
  }, [data])
  useGSAP(() => {
    if (fileName === data?.title) {
      cardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
      gsap.fromTo(
        cardRef.current,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.5,
          ease: 'power2.inOut',
          repeat: 2,
        }
      )
    }
  }, [])

  return (
    <div
      className={clsx(
        'flex w-full',
        'flex-row h-92px bg-gray-100',
        'laptop:flex-col laptop:h-346px laptop:bg-white',
        'scroll-mt-16',
        data?.jumpUrl && 'cursor-pointer'
      )}
      onClick={() => {
        if (data?.jumpUrl) {
          router.push(data?.jumpUrl)
        }
      }}
      ref={cardRef}
    >
      <div
        className={clsx(
          data?.mediaUrl ? 'bg-gray-100' : 'bg-[#576C8A]',
          'flex-none',
          'w-138px h-full',
          'laptop:w-full laptop:h-260px'
        )}
      >
        <PdImage
          src={data?.mediaUrl || '/document_cover.png'}
          enableMask={false}
          object="contain"
          width={'100%'}
          height={'100%'}
        />
      </div>

      <div
        className={clsx(
          'flex-1 flex justify-between items-start',
          'flex-col p-2',
          'laptop:flex-row laptop:pt-4 laptop:pb-6 laptop:pr-4 laptop:pl-0'
        )}
      >
        <div>
          <h4 className={clsx('text-black/90 text-sm font-semibold', 'line-clamp-2', 'laptop:line-clamp-none')}>
            {data?.title}
          </h4>
          <p className={clsx('text-black/60 text-xs font-normal', 'mt-0.5', 'laptop:mt-1')}>{`${data?.fileExt
            .replace('.', '')
            .toLocaleUpperCase()}${fileSize}`}</p>
        </div>

        <div className={clsx('flex items-center gap-3')}>
          <a
            className={clsx('!text-pdblue text-12 flex items-center group cursor-pointer gap-1', 'laptop:text-14')}
            onClick={async (e) => {
              e.stopPropagation()
              const origin = window.location.origin
              const pathname = window.location.pathname
              const params = new URLSearchParams({
                firstTitle: shareParams.firstTitle,
                secondTitle: shareParams.secondTitle,
                thirdTitle: shareParams.thirdTitle,
                fileName: data?.title || '',
              })
              const shareUrl = `${origin}${pathname}?${params.toString()}`
              await navigator.clipboard.writeText(shareUrl)
              toast.success('Copy Success')
            }}
          >
            {t('Share')}
            <i className="inline-block flex-none group-hover:animate-bounce text-sm w-4 h-4 bg-[url('/icons/icon_share.svg')] bg-cover" />
          </a>
          {!!data?.downloadUrl && (
            <a
              className={clsx('!text-pdblue text-12 flex items-center group cursor-pointer gap-1', 'laptop:text-14')}
              onClick={(e) => {
                e.stopPropagation()
                startDownload(data.downloadUrl || '', data?.downloadTitle || data?.title || '')
              }}
            >
              {t('Download')}
              <i className="inline-block flex-none group-hover:animate-bounce text-sm w-3.5 h-4 bg-[url('/icons/icon_arrow_down.svg')] bg-cover" />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

const APPCard: FC<Partial<{ data: DownloadListItem }>> = ({ data }) => {
  const t = useTranslations('Page')

  return (
    <div className={['bg-[#F2F3F5] flex items-center w-full p-2'].join(' ')}>
      <div className={['w-20 h-20 flex-none', 'laptop:w-142px laptop:h-142px'].join(' ')}>
        <PdImage src={data?.qrCodeMedia || ''} enableMask={false} object="cover" width={'100%'} height={'100%'} />
      </div>
      <div
        className={clsx(
          'flex-1 flex items-center gap-2',
          'ml-4 font-semibold',
          'text-14 text-black/60',
          'laptop:font-normal laptop:text-black'
        )}
      >
        <div className={['w-8 h-8'].join(' ')}>
          <PdImage src={data?.mediaUrl || ''} enableMask={false} object="contain" width={'100%'} height={'100%'} />
        </div>
        {data?.title || ''}
      </div>
      {!!data?.jumpUrl && (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a
          href={data.jumpUrl}
          target={'_blank'}
          rel={getLinkRelForHref(data.jumpUrl, 'noreferrer', '_blank')}
          className={clsx(
            'flex-none flex items-center gap-1',
            'group cursor-pointer text-pdblue mr-2 font-normal',
            'text-12',
            'laptop:text-sm'
          )}
        >
          {t('Download')}
          <i className="inline-block flex-none group-hover:animate-bounce text-sm w-3.5 h-4 bg-[url('/icons/icon_arrow_down.svg')] bg-cover" />
        </a>
      )}
    </div>
  )
}

const ArrowIcon: FC<{ isExpand?: boolean }> = ({ isExpand }) => {
  return (
    <div className={clsx('flex items-center')}>
      <svg
        className={`w-5 h-5 transition-transform duration-200 ${isExpand ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  )
}

const TabFrame: FC<PropsWithChildren<TabFrameProps>> = ({
  children,
  init,
  title,
  desc,
  data,
  secondTitle,
  thirdTitle,
  handleSubTitleChange,
  handleThirdTitleChange,
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [activeThirdIndex, setActiveThirdIndex] = useState<string>('0-0')
  const [isExpand, setIsExpand] = useState<Record<number, boolean>>({})

  useEffect(() => {
    const activeIndex = data.findIndex((item) => item.title === secondTitle)
    const activeThirdIndex = data[activeIndex]?.items.findIndex((item) => item.title === thirdTitle)
    setActiveIndex(activeIndex || 0)
    setActiveThirdIndex(`${activeIndex || 0}-${activeThirdIndex || 0}`)
    setIsExpand(
      data.reduce(
        (acc, _, index) => {
          acc[index] = true
          return acc
        },
        {} as Record<number, boolean>
      )
    )
  }, [init, data])

  const handleClick = (secondIndex: number) => {
    setActiveIndex(secondIndex)
    setActiveThirdIndex(`${secondIndex}-0`)
    const activeSencondTitle: string = data[secondIndex]?.title || ''
    handleSubTitleChange(activeSencondTitle)
    handleThirdTitleChange(data[secondIndex]?.items[0]?.title || '')
    setIsExpand((prev) => ({ ...prev, [secondIndex]: !prev[secondIndex] }))
  }

  const handleThirdClick = (secondIndex: number, thirdIndex: number) => {
    setActiveIndex(secondIndex)
    setActiveThirdIndex(`${secondIndex}-${thirdIndex}`)
    const activeThirdTitle: string = data[secondIndex]?.items[thirdIndex]?.title || ''
    handleSubTitleChange(data[secondIndex]?.title || '')
    handleThirdTitleChange(activeThirdTitle)
  }

  const isShowMenu = useMemo(() => {
    if (!data.length) {
      return false
    } else if (data.length === 1) {
      return data[0].items.length > 1
    } else {
      return true
    }
  }, [data])

  return (
    <article
      className={clsx(
        'mx-auto flex',
        'w-full flex-col gap-3 mt-5',
        'laptop:w-300 laptop:flex-row laptop:gap-66px laptop:mt-0'
      )}
    >
      <aside className={clsx('flex-none', 'w-full', 'laptop:w-80')}>
        <div className={clsx('laptop:sticky laptop:top-30')}>
          <h3 className={clsx('break-all text-black/90 font-semibold', 'text-24', 'laptop:text-3xl')}>{title}</h3>
          <p
            className={clsx(
              'w-full text-black/60 font-normal',
              'mt-3 mb-8 text-15',
              'laptop:mt-2 laptop:mb-10 laptop:text-base'
            )}
          >
            {desc}
          </p>
          {data.length > 0 && (
            <>
              <ul className={clsx('hidden', 'laptop:block')}>
                {data.map((item, index) => (
                  <li
                    key={index}
                    className={clsx(
                      'cursor-pointer',
                      'mt-2 first:mt-0',
                      'laptop:font-normal',
                      activeIndex === index ? 'text-blue-600' : 'text-black/90'
                    )}
                  >
                    {isShowMenu && (
                      <>
                        <div className={clsx('flex items-center gap-x-2 py-2')} onClick={() => handleClick(index)}>
                          <span className={clsx('laptop:text-2xl')}>{item.title}</span>
                          {!!item.count && <sup className={clsx('text-sm')}>{item.count}</sup>}
                          {item.items.length > 0 && <ArrowIcon isExpand={isExpand[index]} />}
                        </div>
                        {!!item.items.length && (
                          <ul className={clsx(isExpand[index] ? 'block' : 'hidden')}>
                            {item.items.map((thirdItem, thirdIndex) => (
                              <li
                                key={thirdItem.title}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleThirdClick(index, thirdIndex)
                                }}
                                className={clsx(
                                  'py-1',
                                  'cursor-pointer',
                                  activeThirdIndex === `${index}-${thirdIndex}` ? 'text-blue-600' : 'text-black/90'
                                )}
                              >
                                <span className={clsx('text-base')}>{thirdItem.title}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ul>
              {isShowMenu && (
                <div className="block laptop:hidden">
                  <MobileCascader
                    data={data}
                    secondTitle={secondTitle}
                    thirdTitle={thirdTitle}
                    onSecondTitleChange={handleSubTitleChange}
                    onThirdTitleChange={handleThirdTitleChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </aside>
      <section className={clsx('flex-1', 'h-fit', 'laptop:min-h-125')}>
        {!!data[activeIndex]?.items[Number(activeThirdIndex.split('-')[1])]?.rightDesc && (
          <p
            className={clsx(
              'font-normal',
              'mb-3 text-black/60 text-15',
              'laptop:mb-6 laptop:text-black/90 laptop:text-base'
            )}
          >
            {data[activeIndex]?.items[Number(activeThirdIndex.split('-')[1])]?.rightDesc}
          </p>
        )}

        {children}
      </section>
    </article>
  )
}

const Tab: FC<PropsWithChildren<TabPropsOfBaseType>> = ({
  data, // TabFrame 数据
  list, // 卡片list
  title,
  desc,
  init,
  secondTitle,
  thirdTitle,
  handleSubTitleChange,
  handleThirdTitleChange,
  children,
  startDownload,
}) => {
  return (
    <TabFrame
      title={title}
      desc={desc}
      init={init}
      data={data}
      secondTitle={secondTitle}
      thirdTitle={thirdTitle}
      handleSubTitleChange={handleSubTitleChange}
      handleThirdTitleChange={handleThirdTitleChange}
    >
      <div className={clsx('grid w-full', 'laptop:grid-cols-1 gap-2', 'laptop:grid-cols-2 laptop:gap-4')}>
        {list.map((item, index) =>
          item.type === 'file' ? (
            <BaseCard
              key={index}
              shareParams={{ firstTitle: title, secondTitle, thirdTitle }}
              startDownload={startDownload}
              data={item}
            />
          ) : (
            <APPCard key={index} data={item} />
          )
        )}
      </div>
    </TabFrame>
  )
}

const Download: NextPageWithLayout<{
  data: TDownloadHome
  seoData?: SEOData | null
}> = ({ data: _data, seoData }) => {
  const router = useRouter()
  const { tab, firstTitle, secondTitle, thirdTitle } = router.query
  const isMobileDevice = useMobileDetector()
  const [downloadMeta, setDownloadMeta] = useState<DownloadMetaType>({ show: false, url: '', filename: '' })
  const [listArr, setListArr] = useState<DownloadListItem[]>([])
  const [initFrame, setInitFrame] = useState<boolean>(false)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  // 从 URL 参数获取初始 tab，使用数字索引（1、2、3...）
  const getInitialTabFromUrl = () => {
    const tabParam = tab as string
    if (tabParam && /^\d+$/.test(tabParam)) {
      const tabIndex = parseInt(tabParam, 10) - 1
      if (tabIndex >= 0 && tabIndex < (_data?.items?.length || 0)) {
        return _data?.items?.[tabIndex]?.title || ''
      }
    }
    return null // 没有有效的 tab 参数
  }

  const [activeTab, setActiveTab] = useState<string>(
    getInitialTabFromUrl() || (firstTitle as string) || _data?.items?.[0]?.title || ''
  )
  const [activeSencondTitle, setActiveSencondTitle] = useState<string>(
    (secondTitle as string) || _data?.items?.[0]?.items?.[0]?.title || ''
  )
  const [activeThirdTitle, setActiveThirdTitle] = useState<string>(
    (thirdTitle as string) || _data?.items?.[0]?.items?.[0]?.items?.[0]?.title || ''
  )

  // SlidingTabs 的 ref，用于在 URL 参数变化时（如浏览器后退）同步 tab 位置
  const tabsRef = useRef<SlidingTabsRef>(null)

  const startDownload = (url: string, filename: string) => {
    setDownloadMeta({ url, filename, show: true })
  }

  const data = useMemo<TDownloadHome>(() => {
    return {
      banner: {
        ..._data?.banner,
        mediaUrl: isMobileDevice
          ? _data.banner?.mediaUrlH5 ||
            _data.banner?.mediaUrl ||
            'https://cdn.pudutech.com/website/images/pc/img_download.jpg'
          : _data.banner?.mediaUrl || 'https://cdn.pudutech.com/website/images/pc/img_download.jpg',
      },
      items: _data?.items || [],
      tabTitle: _data?.tabTitle,
    }
  }, [_data, isMobileDevice])

  const handleFetchListData = async (title: string) => {
    const activeTabItem = data.items.find((item) => item.title === activeTab)?.items
    const activeSencondItem = activeTabItem?.find((item) => item.title === activeSencondTitle)?.items
    const files = activeSencondItem?.find((item) => item.title === title)?.files
    if (files) {
      setListArr(files)
    } else {
      setListArr([])
    }
  }

  // tab（一级菜单）切换
  const handleActiveTabChange = (activeIndex: number) => {
    const downloadTabs = document.getElementById('downloadTabs')
    const newTabTitle = data.items?.[activeIndex]?.title || ''
    setActiveTab(newTabTitle)
    setActiveSencondTitle(data.items?.[activeIndex]?.items?.[0]?.title || '')
    setActiveThirdTitle(data.items?.[activeIndex]?.items?.[0]?.items?.[0]?.title || '')
    setInitFrame((pre) => !pre)

    // 更新 URL 参数，使用 history.replaceState 避免触发 Next.js 路由和页面跳动
    // 使用数字索引（1-based）作为参数值，更简洁
    if (isInitialized) {
      const url = new URL(window.location.href)
      url.searchParams.set('tab', String(activeIndex + 1))
      window.history.replaceState(window.history.state, '', url.toString())
    }

    downloadTabs?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  // 二级菜单 变化
  const handleSubTitleChange = (title: string) => {
    const downloadTabs = document.getElementById('downloadTabs')
    setActiveSencondTitle(title)
    downloadTabs?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  // 三级菜单 变化
  const handleThirdTitleChange = (title: string) => {
    const downloadTabs = document.getElementById('downloadTabs')
    setActiveThirdTitle(title)
    downloadTabs?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  useEffect(() => {
    const navbarEle = document.querySelector('#pdNavbar')
    const ssTabsEle = document.querySelector('#downloadTabs')

    if (!ssTabsEle || !navbarEle) {
      return
    }

    const observer = new IntersectionObserver(
      ([e]) => {
        navbarEle.classList.toggle('translate-top-self', e.intersectionRatio < 1 && e.boundingClientRect.top <= 0)
      },
      { threshold: [1] }
    )

    observer.observe(ssTabsEle)
  }, [])

  // 标记组件已初始化，避免首次渲染时触发 URL 更新
  useEffect(() => {
    setIsInitialized(true)
  }, [])

  // 处理 URL tab 参数变化时同步 tab 状态
  useEffect(() => {
    if (!router.isReady || !isInitialized) return
    const tabParam = tab as string
    
    // 只处理数字索引格式的 tab 参数
    if (tabParam && /^\d+$/.test(tabParam)) {
      const newIndex = parseInt(tabParam, 10) - 1
      if (newIndex >= 0 && newIndex < data.items.length) {
        const newTabTitle = data.items[newIndex]?.title || ''
        if (newTabTitle && newTabTitle !== activeTab) {
          setActiveTab(newTabTitle)
          setActiveSencondTitle(data.items[newIndex]?.items?.[0]?.title || '')
          setActiveThirdTitle(data.items[newIndex]?.items?.[0]?.items?.[0]?.title || '')
          setInitFrame((pre) => !pre)
          tabsRef.current?.updatePosition(newIndex, true)
        }
      }
    }
  }, [tab, router.isReady, isInitialized])

  useEffect(() => {
    handleFetchListData(activeThirdTitle)
  }, [activeThirdTitle, firstTitle, secondTitle, thirdTitle])

  return (
    <>
      <Toaster />
      <Head>
        <title>{seoData?.title || data?.tabTitle || 'PUDU - Download'}</title>
      </Head>
      <header
        className={clsx(
          'flex flex-col justify-center',
          'mx-auto text-black/90',
          'w-full h-125px px-6',
          'laptop:max-w-300 laptop:h-75 laptop:px-0',
          'bg-contain bg-no-repeat bg-right-bottom'
        )}
        style={{
          backgroundImage: `url(${data.banner?.mediaUrl})`,
          color: data.banner?.fontColor,
        }}
      >
        <h2 className={clsx('font-semibold text-inherit', 'text-28', 'laptop:text-42')}>{data.banner?.title}</h2>
      </header>

      {/* <DownloadCenter /> */}
      <div id="downloadTabs" className={clsx('bg-white w-full')}>
        <div className={clsx('mx-auto', 'pb-20', 'laptop:w-300 laptop:pb-30')}>
          <SlidingTabs
            ref={tabsRef}
            type="row"
            opacitySwitch={false}
            bgColor="bg-gray-100"
            containerClassName="w-full"
            contentBoxClassName="laptop:mt-6"
            initIndex={data.items.findIndex((item) => item.title === activeTab)}
            childrenBoxClassName={clsx('px-6', 'laptop:px-0')}
            tabsBoxClassName={clsx('bg-white sticky top-0 z-20', 'py-3 laptop:py-6 laptop:justify-start')}
            // tabsContainerClassName="laptop:rounded-full"
            // tabLabels={Object.keys(DownloadType)}
            tabLabels={data.items.map((el) => el.title)}
            tabEleClassName={clsx('!py-6px laptop:!py-2')}
            bgRefClassName={clsx('!h-8', 'laptop:!h-10')}
            activeTabChange={handleActiveTabChange}
          >
            {data.items.map((item, index) => (
              <Tab
                key={index}
                title={item.title}
                desc={item.desc}
                init={initFrame}
                data={item?.items || []}
                list={listArr}
                secondTitle={activeSencondTitle}
                thirdTitle={activeThirdTitle}
                handleSubTitleChange={handleSubTitleChange}
                handleThirdTitleChange={handleThirdTitleChange}
                startDownload={startDownload}
              ></Tab>
            ))}
          </SlidingTabs>
        </div>
      </div>

      <FormBeforeDownload
        show={downloadMeta.show}
        downloadUrl={downloadMeta.url}
        downloadFileName={downloadMeta.filename}
        beforeClose={() => setDownloadMeta({ ...downloadMeta, show: false })}
      />
    </>
  )
}

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const messages = retriveMessages(['support'], locale as LocaleType)
    const region = findRegionByLocale(locale as string) || ''
    let data = {}

    try {
      data = await getDownloadHome(region)
    } catch (err) {
      console.error('getDownloadHome error: ', err)
    }

    return {
      props: {
        messages,
        data,
        region: region || null, // 确保不会传递 undefined 给 JSON 序列化
        seoData: (data as any)?.seoData,
      },
      revalidate: 60,
    }
  },
  () => '/download'
)

Download.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      navbarMoveById="downloadTabs"
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
      containerClassName={styles['download-container']}
    >
      {page}
    </PageLayout>
  )
}

export default Download
